'use client'

import React, { useEffect, useState } from 'react'
import { DataTable, DataTableProps } from './DataTable'
import { ErrorState, InlineEmpty, SkeletonCard } from './States'

export interface ResponsiveTableProps<T> extends DataTableProps<T> {
  /** The card shown in place of a row below `md`. Usually a RecordCard. */
  renderMobileCard: (row: T, index: number) => React.ReactNode
  /**
   * Reveal this many cards at a time on mobile, with a "Load more" button.
   * 0 shows everything — right for a paginated table, wrong for a long list
   * that would otherwise bury whatever comes after it.
   */
  mobileStep?: number
  mobileMoreLabel?: string
  emptyMessage?: string
  error?: string | null
  onRetry?: () => void
  /** Footer slot, normally <Pagination/>. Hidden while loading or empty. */
  footer?: React.ReactNode
  mobileSkeletonHeight?: string
}

/**
 * A dashboard list: a grid table on a pointer, a stack of cards on a phone.
 *
 * Both halves render from the same `data`, so they cannot drift, and the switch
 * is pure CSS (`hidden md:block` / `md:hidden`) rather than a media-query hook —
 * which means the correct one is in the markup on first paint and there is no
 * flash of the wrong layout while a client effect resolves.
 *
 * Loading, error and empty are handled here rather than by each caller, because
 * every screen that open-coded them ended up with a slightly different box.
 */
export function ResponsiveTable<T>({
  renderMobileCard,
  mobileStep = 0,
  mobileMoreLabel = 'Load more',
  emptyMessage = 'Nothing to show yet.',
  error = null,
  onRetry,
  footer,
  mobileSkeletonHeight = 'h-[170px]',
  ...tableProps
}: ResponsiveTableProps<T>) {
  const { data, isLoading = false, skeletonRows = 6 } = tableProps
  const [visible, setVisible] = useState(mobileStep || data.length)

  // A filter change swaps the data under us; without this the list stays
  // truncated at whatever the previous "Load more" left it at.
  useEffect(() => {
    setVisible(mobileStep || data.length)
  }, [data, mobileStep])

  if (error) return <ErrorState message={error} onRetry={onRetry} />

  const shown = mobileStep ? data.slice(0, visible) : data
  const isEmpty = !isLoading && data.length === 0

  return (
    <>
      <div className="hidden md:block">
        <DataTable {...tableProps} />
      </div>

      <div className="md:hidden flex flex-col gap-3 px-4 py-4">
        {isLoading
          ? Array.from({ length: Math.min(skeletonRows, 4) }).map((_, i) => (
              <SkeletonCard key={i} height={mobileSkeletonHeight} />
            ))
          : shown.map((row, i) => (
              <React.Fragment key={i}>{renderMobileCard(row, i)}</React.Fragment>
            ))}

        {!isLoading && mobileStep > 0 && visible < data.length && (
          <button
            type="button"
            onClick={() => setVisible((v) => v + mobileStep)}
            className="w-full h-11 rounded-xl border border-dashboard-hairline bg-white text-[13px] font-medium text-dashboard-text font-poppins hover:bg-slate-50"
          >
            {mobileMoreLabel}
          </button>
        )}
      </div>

      {isEmpty && <InlineEmpty message={emptyMessage} />}
      {!isLoading && !isEmpty && footer}
    </>
  )
}
